import { Heading, VStack, useToast } from '@chakra-ui/react';
import { Loader } from '@jurnee/common/src/components/Loader';
import { PayoutBeneficiaryJSON } from '@jurnee/common/src/entities/PayoutBeneficiary';
import { getErrorToast } from '@jurnee/common/src/utils/toasts';
import { getPayoutBeneficiaries } from 'api/payoutBeneficiaries';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import { getPartnerSelector } from 'store/partner/partner.selectors';
import { AddPayoutInformation } from './AddPayoutInformation';
import { PayoutInformation } from './PayoutInformation';

export function PayoutInformationCard() {
  const toast = useToast();
  const { t } = useTranslation(['payoutInformation', 'common']);

  const [searchParams] = useSearchParams();
  const payoutInfoDrawerOpen = searchParams.get('payoutInfoDrawerOpen') === 'true';

  const partner = useSelector(getPartnerSelector);
  const [payoutBeneficiaries, setPayoutBeneficiaries] = useState<PayoutBeneficiaryJSON[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  async function fetchPayoutBeneficiaries() {
    try {
      if (!partner) {
        return;
      }
      const beneficiaries = await getPayoutBeneficiaries(partner.id);
      setPayoutBeneficiaries(beneficiaries.list);
      setIsLoading(false);
    } catch (err: unknown) {
      toast(getErrorToast(t('fetchPayoutInfo.error')));
      setIsLoading(false);
    }
  }

  useEffect(() => {
    fetchPayoutBeneficiaries();
  }, []);

  function onUpsertBeneficiary(beneficiary: PayoutBeneficiaryJSON) {
    setPayoutBeneficiaries((prev) => {
      const isExisting = prev.some(b => b.id === beneficiary.id);

      if (isExisting) {
        return prev.map(b => (b.id === beneficiary.id ? beneficiary : b));
      }

      return [...prev, beneficiary];
    });
  }

  function renderContent() {
    if (isLoading) {
      return <Loader
        w="100%"
        py="90px"
        bg="white"
        border="1px solid"
        borderColor="gray.200"
        borderRadius={4}
      />;
    }

    if (payoutBeneficiaries.length === 0) {
      return <AddPayoutInformation
        onUpsert={onUpsertBeneficiary}
        payoutInfoDrawerOpen={!!payoutInfoDrawerOpen}
      /> ;
    }

    return (
      <VStack w="100%" spacing={4}>
        {
          payoutBeneficiaries.map((beneficiary) => (
            <PayoutInformation
              key={beneficiary.id}
              beneficiary={beneficiary}
              onUpsert={onUpsertBeneficiary}
            />
          ))
        }

        <AddPayoutInformation
          onUpsert={onUpsertBeneficiary}
          payoutInfoDrawerOpen={!!payoutInfoDrawerOpen}
        />
      </VStack>
    );
  }

  return (
    <VStack w="100%" spacing={2} alignItems="flex-start">
      <Heading size="md">{t('drawer.title')}</Heading>

      { renderContent() }
    </VStack>
  );
}